import {
  Shell,
  type DemoEntrees,
  navigation,
  resolveRoute,
  type QueryClient,
  type RouteResolution,
  type SessionStatus,
} from "@hakili/app-shell";
import { t } from "@hakili/i18n";
import {
  RAIL_UNBUILT,
  RAIL_ACTIVE,
  PageColumn,
  RAIL_ENTRY,
  RAIL_FOOT_BAR,
  RAIL_FOOT_ICON,
  RAIL_MARK,
  RAIL_FRAME,
  RailAvatarMenu,
  RAIL_IDLE,
  RAIL_NAV,
  useDemandeFonctionnalite,
} from "@hakili/ui";
import {
  Inbox,
  Landmark,
  Lightbulb,
  Sparkles,
  Target,
  TrendingUp,
  User,
  UserPlus,
  Users,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { VueSwitcher } from "./VueSwitcher.js";
import { panneauDe } from "./panel.js";
import type { ConsoleStore } from "./store.js";
import { cheminDAtterrissage, ecransDeLaVue, routesPourLaVue, type Ecran, type Vue } from "./views.js";

/**
 * `P0-UIK-014`: the console's own chrome.
 *
 * 4.6.4: *"Left nav rail derived from the role's screen whitelist, `max-w-6xl`
 * content, a right side panel for the conversation."* (amendé le 18 octobre 2026
 * (D-1018-6): the content column is the kit's `PageColumn`, `max-w-7xl`.) The composition
 * (`AuthGate`, store, error boundary, query client) is `@hakili/app-shell`'s
 * and is not repeated here; `DECISION ADDED A35` puts the plumbing there and
 * the LOOK in the app, which is why this file exists at all.
 *
 * **Danger sentence.** The nav rail must be computed from the same whitelist
 * the guard consults, and `navigation()` is that function. A rail built from
 * its own list drifts, and the drift is asymmetric: a dead entry is reported
 * within the hour, while a screen that is hidden but reachable is reported by
 * an auditor months later, if at all.
 */

const NAV_KEY: Readonly<Record<Ecran, string>> = {
  collecte: "console.nav.collecte",
  arrivees: "console.nav.arrivees",
  priorites: "console.nav.priorites",
  partenaires: "console.nav.partenaires",
  developpement: "console.nav.developpement",
  expertises: "console.nav.expertises",
  equipe: "console.nav.equipe",
  comptes: "console.nav.comptes",
  supervision: "console.nav.supervision",
  fonctionnalites: "console.nav.fonctionnalites",
  profil: "console.nav.profil",
};

/**
 * The nav label for a screen. Exhaustive by type: a new screen fails the build
 * here. **The chargé's first item reads « Mes dossiers »** , the drawing's own
 * `NavRail` renames it for that view alone, because what he sees IS his own.
 */
export const libelleEcran = (ecran: Ecran, vue?: Vue): string =>
  ecran === "collecte" && vue === "collecte" ? t("console.nav.collecteMien") : t(NAV_KEY[ecran]);

/**
 * `round CN-A`, `CN-A.0`: the rail's icons, **the drawing's own, by name**.
 *
 * `hakili-console.jsx` imports them from `lucide-react` at the top of the file
 * and this map names the same ones. **`OD-19` was closed at Round F2** ,
 * lucide is adopted for the six apps, pinned at `0.454.0`, imported by name,
 * and the hand-drawn glyphs were deleted on adoption. Drawing eleven of them
 * inline here was re-opening a decision the record had closed, and it is the
 * architect's correction on `7deac7b`.
 *
 * **Exhaustive by type**, which is a property of this MAP and not a reason to
 * draw: a twelfth screen fails the build here rather than rendering nothing.
 */
const ICONE: Readonly<Record<Ecran, LucideIcon>> = {
  collecte: Inbox,
  arrivees: UserPlus,
  priorites: Target,
  partenaires: Landmark,
  developpement: TrendingUp,
  expertises: ShieldCheck,
  equipe: Users,
  comptes: Users,
  supervision: Sparkles,
  fonctionnalites: Lightbulb,
  profil: User,
};

/** One rail icon, read off the map. */
function Icone({ ecran }: { readonly ecran: Ecran }): React.JSX.Element {
  const Glyphe = ICONE[ecran];
  return <Glyphe className="h-5 w-5" />;
}

/**
 * `P1-CNW-019`: **the screens this bundle actually serves.**
 *
 * The rail is derived from `/me`'s whitelist , the ROLE's entitlement , and the
 * bundle's inventory is a different set entirely. The sitting of 30 August found
 * the gap by clicking: the founder's `/me` lists `partenaires`, the chip
 * rendered, the click landed back on `Profil`, and nothing said why.
 *
 * **A dead link should say so, not shrug.** So an entitled screen nobody has
 * built renders as a DISABLED chip carrying `console.session.ecranAbsent`, and
 * the rail stops offering doors that are painted on.
 *
 * The set is passed in rather than imported, because `App.tsx` owns
 * `ECRANS_CONSTRUITS` , *"this set is the ONLY place that knowledge lives"* ,
 * and two copies of an inventory drift the day a screen lands.
 */
export interface ConsoleShellProps {
  readonly vue: Vue;
  /** `P0-UIK-018`. The switcher renders only when BOTH conditions hold. */
  readonly demo: DemoEntrees;
  /** Changes the demonstrated view. Never called in production: there is no control to call it. */
  readonly choisirVue: (vue: Vue) => void;
  readonly queryClient: QueryClient;
  readonly store: ConsoleStore;
  readonly status: SessionStatus;
  /** The current location, split the way a router hands it over. */
  readonly path: string;
  readonly search: string;
  readonly pending: ReactNode;
  readonly signIn: ReactNode;
  /** Rendered beside the screen when the URL asks for a panel (4.6.4). */
  readonly renderPanel: (panneau: "conversation") => ReactNode;
  /** `P1-CNW-019`: the screens this bundle serves. Owned by `App.tsx`. */
  readonly construits: ReadonlySet<string>;
  /**
   * `00-SPINE` 6.2. The SAME function the profile screen calls , it revokes
   * server side before forgetting. A rail control that only cleared the tab
   * would look identical and be no logout at all.
   */
  readonly deconnecter: () => void;
  /** The person, from `/me` , the rail's initials and the banner's name. */
  readonly personne: string;
  readonly children: ReactNode;
}

/**
 * The initials the rail's avatar carries, DERIVED from the name `/me` answers.
 *
 * The drawing stores them beside each person; a screen cannot, because the name
 * is the door's and the initials are a rendering of it. One word gives one
 * letter , a person called « Kadia » is `K` and never `KA`, which would invent
 * a surname.
 */
export function initialesDe(nom: string): string {
  const mots = nom.trim().split(/\s+/u).filter(Boolean);
  if (mots.length === 0) return "";
  const premier = mots[0]?.[0] ?? "";
  const dernier = mots.length > 1 ? (mots[mots.length - 1]?.[0] ?? "") : "";
  return (premier + dernier).toLocaleUpperCase("fr");
}

export function ConsoleShell({
  vue,
  demo,
  choisirVue,
  queryClient,
  store,
  status,
  path,
  search,
  pending,
  signIn,
  renderPanel,
  children,
  construits,
  deconnecter,
  personne,
}: ConsoleShellProps): React.JSX.Element {
  const table = routesPourLaVue(vue);
  const whitelist = ecransDeLaVue(vue);
  const resolution = resolveRoute(table, path, whitelist);
  const panneau = panneauDe(search);
  // The mark returns to THIS view's landing , `collecte` does not exist for the
  // two relation views, and a mark that always went there would be a dead link
  // for two of the five people who use it.
  const landing = cheminDAtterrissage(vue);
  // `D-1019-1`: « Profil » is not an entry of the rail; the avatar's menu opens it, when the actor is entitled to it.
  const profil = navigation(table, whitelist).find((route) => route.screen === "profil");
  // `@hakili/ui`'s own provider holds the modal and the door; the rail only
  // opens it with an address. A second form here would be a second shape for
  // one workflow.
  const { ouvrir } = useDemandeFonctionnalite();

  return (
    <Shell
      queryClient={queryClient}
      store={store}
      status={status}
      pending={pending}
      signIn={signIn}
      // `P0-CNW-027`: a public route's token is its authorisation, so the gate
      // lets its holder through instead of showing them a sign-in whose
      // password the screen behind it exists to set.
      isPublicRoute={resolution.kind === "render" && resolution.route.public === true}
      crashMessage={t("common.error.inattendue")}
      renderError={(fallback) => (
        <p role="alert" className="p-6 text-sm text-gray-500">
          {fallback.message}
        </p>
      )}
      layout={(outlet) => (
        <div className="flex min-h-screen bg-gray-50">
          {/**
           * `round CN-A`, `CN-A.0`: **the rail as the drawing draws it.**
           *
           * `hakili-console.jsx`'s `NavRail` is narrow , `w-20` , and every
           * entry is an icon over a small word, because eleven entries in a
           * `w-56` column push the content off a 1366 screen. The mark at the
           * top returns to the VIEW's own landing, which is not `collecte` for
           * the two relation views; « interne » sits under it, and the person's
           * initials at the foot.
           *
           * **What is derived and what is drawn.** The ENTRIES are
           * `navigation(table, whitelist)` , the same whitelist the guard
           * consults, never a list of this file's , and that is unchanged from
           * `P0-UIK-014`. What this round changes is only the shape.
           */}
          <aside aria-label={t("console.nav.collecte")} className={RAIL_FRAME}>
            <a
              href={landing}
              className="mb-1 shrink-0 no-underline"
              data-testid="nav-marque"
              aria-label="Hakili"
            >
              <span className="text-xl font-bold tracking-tight text-indigo-600">H</span>
            </a>
            <span className={RAIL_MARK}>{t("console.nav.interne")}</span>

            <nav className={RAIL_NAV}>
              {navigation(table, whitelist)
                .filter((route) => route.screen !== "profil")
                .map((route) => {
                  const servi = construits.has(route.screen);
                  const courant = resolution.kind === "render" && resolution.route.screen === route.screen;
                  return servi ? (
                    <a
                      key={route.screen}
                      href={route.path}
                      aria-current={courant ? "page" : undefined}
                      className={`${RAIL_ENTRY} ${courant ? RAIL_ACTIVE : RAIL_IDLE}`}
                      data-testid={`nav-${route.screen}`}
                    >
                      <Icone ecran={route.screen as Ecran} />
                      <span className="text-xs font-medium">{libelleEcran(route.screen as Ecran, vue)}</span>
                    </a>
                  ) : (
                    /**
                     * Entitled and unbuilt, and it now says WHICH ROUND builds
                     * it. « Pas encore construit » tells a founder the thing is
                     * missing; it does not tell him whether it is coming, and
                     * the difference between « forgotten » and « next » is the
                     * whole of what he wants to know.
                     *
                     * A `span` and not a disabled `a`: an anchor without an href
                     * is still focusable in some readers, and this is not a
                     * control.
                     */
                    <span
                      key={route.screen}
                      aria-disabled="true"
                      title={t("console.session.ecranRond")}
                      className={`${RAIL_ENTRY} ${RAIL_UNBUILT}`}
                      data-testid={`nav-${route.screen}-absent`}
                    >
                      <Icone ecran={route.screen as Ecran} />
                      <span className="text-xs font-medium">{libelleEcran(route.screen as Ecran, vue)}</span>
                    </span>
                  );
                })}
            </nav>

            <div className={RAIL_FOOT_BAR}>
              {/* `WF-CON-24`: the feedback channel, in the rail so it is reachable
                  from every screen, and carrying the screen a person is looking
                  at. Buried in the Profil it collects nothing. */}
              <button
                type="button"
                onClick={() => {
                  /**
                   * `WF-CON-43`'s acceptance criterion, and the whole reason
                   * `shell/demande.ts` takes the screen as a VALUE: read when the
                   * modal renders, the answer has already become « the modal ».
                   * Captured here, at the click.
                   */
                  ouvrir(
                    `console:${resolution.kind === "render" ? resolution.route.screen : "inconnu"}:une-idee`,
                  );
                }}
                title={t("console.nav.demanderFonctionnalite")}
                aria-label={t("console.nav.uneIdee")}
                className={RAIL_FOOT_ICON}
                data-testid="nav-une-idee"
              >
                <Lightbulb className="h-5 w-5" aria-hidden="true" />
              </button>

              {/**
               * `00-SPINE` 6.2, and it stays on the surface. The drawing has no
               * logout in its rail , it has a bell and an avatar , but the
               * founder's law is that every surface can be LEFT, and a drawing
               * that omits the way out is a drawing of a session nobody closes.
               * **Divergence from the drawing, deliberate, and the law wins.**
               * Since `D-1019-1` the way out lives in the AVATAR's menu beside
               * « Profil », which left the list of entries: the person is one
               * control, and what is hers is behind it.
               */}
              <RailAvatarMenu
                initials={initialesDe(personne)}
                name={personne}
                tone="bg-indigo-700 text-white"
                testId="nav-initiales"
                items={[
                  ...(profil === undefined
                    ? []
                    : [
                        {
                          label: t("console.nav.profil"),
                          testId: "nav-profil",
                          onSelect: () => {
                            window.location.assign(profil.path);
                          },
                        },
                      ]),
                  {
                    label: t("console.profil.deconnexion"),
                    testId: "nav-deconnexion",
                    onSelect: deconnecter,
                  },
                ]}
              />
            </div>
          </aside>

          {/* 4.6.4's content column, amendé le 18 octobre 2026 (D-1018-6): it was `max-w-6xl`, it is the
              kit's `PageColumn` , `max-w-7xl` , and no screen sets a width of its own inside it. */}
          <main className="min-w-0 flex-1">
            <PageColumn>{outlet}</PageColumn>
          </main>

          {/* 03, 2.0's demonstration device, and 4.7.3's flag decides whether it
              exists at all. In a production build `VueSwitcher` returns null,
              so there is nothing here to un-hide. */}
          <div className="fixed inset-x-0 bottom-0">
            <VueSwitcher demo={demo} vue={vue} choisir={choisirVue} />
          </div>

          {/* The panel is full screen height with its OWN scroll, so the page
              never grows with the number of messages , 4.6.4's sentence, and
              the reason it is a sibling of `main` rather than inside it. */}
          {panneau === null ? null : (
            <aside
              aria-label={t("console.panel.conversation")}
              className="h-screen w-96 shrink-0 overflow-y-auto border-l border-gray-200 bg-white"
            >
              {renderPanel(panneau)}
            </aside>
          )}
        </div>
      )}
    >
      {children}
    </Shell>
  );
}

/**
 * Where the console should be, given a URL and a view: the screen, or the
 * redirect 4.6.4 mandates.
 *
 * Exported beside the component because a router needs the ANSWER before it
 * renders anything, and a redirect performed during render is a redirect that
 * has already mounted the screen it was meant to prevent.
 */
export const resoudre = (vue: Vue, path: string): RouteResolution =>
  resolveRoute(routesPourLaVue(vue), path, ecransDeLaVue(vue));
